import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Comment, CommentAnchor } from '../api/client';
import { api } from '../api/client';
import { useArtifact, useMe, useMembers } from '../hooks/useApi';
import posthog from '../lib/posthog';
import CommentSidebar from '../components/CommentSidebar';
import VersionHistory from '../components/VersionHistory';
import SelectionComposer from '../components/comments/SelectionComposer';
import CommentMarkers from '../components/comments/CommentMarkers';
import CommentModeToolbar from '../components/comments/CommentModeToolbar';
import ShareDialog from '../components/ShareDialog';
// import ArtifactActionsMenu from '../components/ArtifactActionsMenu';
import { ARTIFACT_ORIGIN } from '../env';
import { getResolvedTheme, getThemePreference, subscribeToThemePreference } from '../theme';
import { relativeTime } from '../utils';
import { useResolvedCommentAnchors } from '../hooks/useResolvedCommentAnchors';

const SIDEBAR_WIDTH = 320;
const MIN_IFRAME_WIDTH = 640;

const getFileType = (contentType?: string): string => {
  if (!contentType) return 'HTML';
  if (contentType.includes('pdf')) return 'PDF';
  if (contentType.includes('markdown')) return 'MD';
  return 'HTML';
};

const fileTypeBadgeStyles: Record<string, { background: string; color: string }> = {
  HTML: { background: 'var(--rd-badge-html-bg)', color: 'var(--rd-badge-html-text)' },
  MD:   { background: 'var(--rd-badge-md-bg)', color: 'var(--rd-badge-md-text)' },
  PDF:  { background: 'var(--rd-badge-pdf-bg)', color: 'var(--rd-badge-pdf-text)' },
};

function isTypingTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && !!target.closest('input, textarea, select, [contenteditable="true"]');
}

function findCommentById(comments: Comment[], commentId: string | null): Comment | null {
  if (!commentId) return null;
  for (const comment of comments) {
    if (comment.id === commentId) return comment;
    const reply = findCommentById(comment.replies ?? [], commentId);
    if (reply) return reply;
  }
  return null;
}

export default function ArtifactViewer() {
  const { id, version: versionParam } = useParams<{
    id: string;
    version: string;
  }>();
  const navigate = useNavigate();
  const { data: meData } = useMe();
  const { members } = useMembers();
  const { artifact, loading, error, refetch: refetchArtifact } = useArtifact(id!);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [commentMode, setCommentMode] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [starred, setStarred] = useState(false);
  const [starring, setStarring] = useState(false);
  const [pendingAnchor, setPendingAnchor] = useState<CommentAnchor | null>(null);
  const [selectionRect, setSelectionRect] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [cspViolations, setCspViolations] = useState<{ blocked: string[]; nonBlocking: string[]; prompt: string }>({ blocked: [], nonBlocking: [], prompt: '' });
  const [cspPopoverOpen, setCspPopoverOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [presenting, setPresenting] = useState(false);
  const [presentExitVisible, setPresentExitVisible] = useState(false);
  const presentExitTimerRef = useRef<number | null>(null);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() =>
    getResolvedTheme(getThemePreference()),
  );
  // Open comments from the sidebar, used for rendering markers
  const [openComments, setOpenComments] = useState<Comment[]>([]);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);
  const [hoveredCommentId, setHoveredCommentId] = useState<string | null>(null);
  const [scrollToCommentId, setScrollToCommentId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const highlightedCommentId = hoveredCommentId ?? selectedCommentId;

  const activeVersion = versionParam
    ? parseInt(versionParam, 10)
    : artifact?.current_version ?? 1;

  const latestVersion = artifact?.current_version ?? 1;
  const sortedVersions = (artifact?.versions ?? [])
    .slice()
    .sort((a, b) => a.version_number - b.version_number);
  const activeVersionIndex = sortedVersions.findIndex((version) => version.version_number === activeVersion);
  const versionWindowStart = Math.max(
    0,
    Math.min(
      activeVersionIndex === -1 ? sortedVersions.length - 5 : activeVersionIndex - 2,
      sortedVersions.length - 5,
    ),
  );
  const visibleVersions =
    sortedVersions.length <= 5
      ? sortedVersions
      : sortedVersions.slice(versionWindowStart, versionWindowStart + 5);

  // Comment refresh trigger — sidebar manages its own fetching now
  const [commentRefreshKey, setCommentRefreshKey] = useState(0);
  const handleCommentsChanged = useCallback(() => {
    setCommentRefreshKey((k) => k + 1);
    setPendingAnchor(null);
    setSelectionRect(null);
    setSelectedCommentId(null);
    setHoveredCommentId(null);
    setScrollToCommentId(null);
  }, []);

  useEffect(() => {
    if (!artifact?.id) return;
    let cancelled = false;

    api.listComments(artifact.id, undefined, false)
      .then(({ comments }) => {
        if (!cancelled) setOpenComments(comments);
      })
      .catch(() => {
        if (!cancelled) setOpenComments([]);
      });

    return () => {
      cancelled = true;
    };
  }, [artifact?.id, commentRefreshKey]);

  // Monitor container width for responsive sidebar collapse
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Auto-collapse sidebar if not enough room
  const shouldAutoCollapse =
    containerWidth > 0 &&
    containerWidth - SIDEBAR_WIDTH < MIN_IFRAME_WIDTH;

  const showDesktopSidePanel = !shouldAutoCollapse && (sidebarOpen || showVersionHistory);
  const showStackedPanel = shouldAutoCollapse && (sidebarOpen || showVersionHistory);

  // Send message to the artifact iframe
  const postToIframe = useCallback((msg: Record<string, unknown>) => {
    iframeRef.current?.contentWindow?.postMessage(msg, '*');
  }, []);

  const { resolvedPositions, hasResolverResponded, iframeScroll } = useResolvedCommentAnchors({
    iframeLoaded,
    openComments,
    currentVersion: activeVersion,
    postToIframe,
  });

  const highlightCommentAnchor = useCallback(
    (comment: Comment, scroll: boolean) => {
      if (!comment.anchor) {
        postToIframe({ type: 'exhibit:clear-highlights' });
        return;
      }
      postToIframe({
        type: 'exhibit:highlight-anchor',
        payload: { anchor: comment.anchor, scroll },
      });
    },
    [postToIframe],
  );

  const restoreSelectedAnchorHighlight = useCallback(() => {
    const selectedComment = findCommentById(openComments, selectedCommentId);
    if (selectedComment) {
      highlightCommentAnchor(selectedComment, false);
      return;
    }
    postToIframe({ type: 'exhibit:clear-highlights' });
  }, [highlightCommentAnchor, openComments, postToIframe, selectedCommentId]);

  const handleLeaveAnchor = useCallback(() => {
    restoreSelectedAnchorHighlight();
  }, [restoreSelectedAnchorHighlight]);

  const handleMarkerHover = useCallback(
    (comment: Comment) => {
      highlightCommentAnchor(comment, false);
    },
    [highlightCommentAnchor],
  );

  const handleSidebarCommentHover = useCallback(
    (comment: Comment) => {
      setHoveredCommentId(comment.id);
      highlightCommentAnchor(comment, false);
    },
    [highlightCommentAnchor],
  );

  const handleSidebarCommentLeave = useCallback(
    (comment: Comment) => {
      setHoveredCommentId((current) => (current === comment.id ? null : current));
      restoreSelectedAnchorHighlight();
    },
    [restoreSelectedAnchorHighlight],
  );

  const handleSidebarCommentFocus = useCallback(
    (comment: Comment) => {
      setHoveredCommentId(null);
      setSelectedCommentId(comment.id);
      highlightCommentAnchor(comment, true);
    },
    [highlightCommentAnchor],
  );

  const handleMarkerClick = useCallback(
    (commentId: string) => {
      // If sidebar is open and clicking the same marker, close it
      if (sidebarOpen && selectedCommentId === commentId) {
        setSidebarOpen(false);
        setSelectedCommentId(null);
        setHoveredCommentId(null);
        setScrollToCommentId(null);
        postToIframe({ type: 'exhibit:clear-highlights' });
        return;
      }
      setShowVersionHistory(false);
      setSelectedCommentId(commentId);
      setHoveredCommentId(null);
      setScrollToCommentId(commentId);
      const comment = findCommentById(openComments, commentId);
      if (comment) highlightCommentAnchor(comment, true);
      if (!sidebarOpen) setSidebarOpen(true);
    },
    [highlightCommentAnchor, openComments, postToIframe, selectedCommentId, sidebarOpen],
  );

  // Listen for postMessage from artifact iframe
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (ARTIFACT_ORIGIN && event.origin !== ARTIFACT_ORIGIN) return;
      const { type, payload } = event.data || {};
      if (type === 'artifact:loaded') {
        setIframeLoaded(true);
      } else if (type === 'artifact:error') {
        console.error('Artifact load error:', event.data);
      } else if (type === 'exhibit:text-selected') {
        const { text, prefix, suffix, xpath, startOffset, endOffset, scrollY, rect } = payload;
        setPendingAnchor({
          type: 'text',
          text,
          prefix,
          suffix,
          xpath,
          startOffset,
          endOffset,
          scrollY: scrollY ?? 0,
          rect: rect ? { x: rect.x, y: rect.y, width: rect.width, height: rect.height } : undefined,
        });
        setSelectionRect(rect);
      } else if (type === 'exhibit:element-selected') {
        if (!commentMode || payload?.anchor?.type !== 'element' || !payload?.anchor?.xpath || !payload?.rect) return;
        const { anchor, rect } = payload;
        setPendingAnchor({
          type: 'element',
          xpath: anchor.xpath,
          label: anchor.label,
          tagName: anchor.tagName,
          text: anchor.text,
          fingerprint: anchor.fingerprint,
          rect: { x: rect.x, y: rect.y, width: rect.width, height: rect.height },
        });
        setSelectionRect(rect);
      } else if (type === 'exhibit:selection-cleared') {
        setPendingAnchor(null);
        setSelectionRect(null);
      } else if (type === 'exhibit:csp-violation') {
        setCspViolations(payload);
      } else if (type === 'exhibit:marker-clicked') {
        if (payload?.commentId) handleMarkerClick(payload.commentId);
      }
    },
    [commentMode, handleMarkerClick],
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  // Check for new versions periodically
  useEffect(() => {
    const interval = setInterval(() => {
      refetchArtifact();
    }, 30000);
    return () => clearInterval(interval);
  }, [refetchArtifact]);

  const handleVersionChange = (version: number) => {
    setIframeLoaded(false);
    posthog.capture('artifact_version_changed', { artifact_id: id, version });
    if (version === latestVersion) {
      navigate(`/a/${id}`);
    } else {
      navigate(`/a/${id}/v/${version}`);
    }
  };

  const handleVersionPreview = useCallback((versionId: string) => {
    const version = artifact?.versions?.find((v) => v.id === versionId);
    if (version) {
      handleVersionChange(version.version_number);
    }
  }, [artifact?.versions]);

  const handleRestoreVersion = useCallback(async (versionId: string) => {
    if (!artifact) return;
    await api.restoreVersion(artifact.id, versionId);
    posthog.capture('artifact_version_restored', { artifact_id: artifact.id, artifact_title: artifact.title, version_id: versionId });
    await refetchArtifact();
    // Navigate to latest (the restored version is now latest)
    navigate(`/a/${id}`);
  }, [artifact, id, navigate, refetchArtifact]);

  useEffect(() => {
    if (!artifact?.id) return;
    api.createArtifactAccessSession(artifact.id)
      .then((session) => setAccessToken(session.token))
      .catch(() => setAccessToken(null));
  }, [artifact?.id]);

  useEffect(() => {
    return subscribeToThemePreference((preference) => {
      setResolvedTheme(getResolvedTheme(preference));
    });
  }, []);

  const activeVersionId =
    artifact?.versions?.find((v) => v.version_number === activeVersion)?.id
      ?? artifact?.versions?.[0]?.id
      ?? null;

  useEffect(() => {
    setStarred(artifact?.starred ?? false);
  }, [artifact?.starred]);

  const iframeSrc = artifact && accessToken && activeVersionId
    ? `${ARTIFACT_ORIGIN}/t/${accessToken}/${artifact.id}/${activeVersionId}/`
    : '';

  // Detect PDF artifacts: check if title ends with .pdf or description mentions PDF
  // The Artifact Worker serves PDFs without the postMessage wrapper, so commenting
  // anchoring features (pin, text selection) don't work on PDFs
  // Detect content type from API response (set by R2 file inspection)
  const contentType = (artifact as Record<string, unknown> | null)?.content_type as string || 'text/html';
  const isPdf = contentType === 'application/pdf';
  const isMarkdown = contentType.includes('markdown');
  const fileType = getFileType(contentType);

  const selectCursorTool = useCallback(() => {
    setPendingAnchor(null);
    setSelectionRect(null);
    setCommentMode(false);
  }, []);

  const selectCommentTool = useCallback(() => {
    if (isPdf) return;
    setPendingAnchor(null);
    setSelectionRect(null);
    setCommentMode(true);
  }, [isPdf]);

  useEffect(() => {
    if (isPdf && commentMode) setCommentMode(false);
  }, [commentMode, isPdf]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;
      if (presenting) {
        if (event.key === 'Escape') {
          event.preventDefault();
          setPresenting(false);
        }
        return;
      }
      const key = event.key.toLowerCase();
      const hasModifier = event.metaKey || event.ctrlKey || event.altKey;
      if (key === 'c' && !isPdf && !hasModifier) {
        event.preventDefault();
        selectCommentTool();
      } else if (key === 'v' && !hasModifier) {
        event.preventDefault();
        selectCursorTool();
      } else if (event.key === 'Escape' && commentMode) {
        event.preventDefault();
        selectCursorTool();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [commentMode, isPdf, presenting, selectCommentTool, selectCursorTool]);

  useEffect(() => {
    if (!iframeLoaded || isPdf) return;
    postToIframe({ type: 'exhibit:set-comment-mode', payload: { enabled: commentMode } });
  }, [commentMode, iframeLoaded, isPdf, postToIframe]);

  useEffect(() => {
    if (!iframeLoaded || !isMarkdown) return;
    postToIframe({ type: 'exhibit:set-theme', payload: { theme: resolvedTheme } });
  }, [iframeLoaded, isMarkdown, postToIframe, resolvedTheme]);

  const handleToggleStar = useCallback(async () => {
    if (!artifact || starring) return;
    const nextStarred = !starred;
    setStarred(nextStarred);
    setStarring(true);
    try {
      if (nextStarred) {
        await api.starArtifact(artifact.id);
        posthog.capture('artifact_starred', { artifact_id: artifact.id, artifact_title: artifact.title });
      } else {
        await api.unstarArtifact(artifact.id);
        posthog.capture('artifact_unstarred', { artifact_id: artifact.id, artifact_title: artifact.title });
      }
      refetchArtifact();
    } catch {
      setStarred(!nextStarred);
    } finally {
      setStarring(false);
    }
  }, [artifact, refetchArtifact, starred, starring]);

  // Handle inline comment submission from the selection composer
  const handleInlineCommentSubmit = useCallback(
    async (body: string, mentions: Array<{ id: string; name: string }>) => {
      if (!artifact || !pendingAnchor) return;
      await api.createComment(
        artifact.id,
        body,
        activeVersion,
        undefined,
        pendingAnchor,
        mentions.length > 0 ? mentions : undefined,
      );
      posthog.capture('comment_created', {
        artifact_id: artifact.id,
        artifact_title: artifact.title,
        version: activeVersion,
        has_mentions: mentions.length > 0,
        anchor_type: pendingAnchor.type,
      });
      setPendingAnchor(null);
      setSelectionRect(null);
      handleCommentsChanged();
      // Open sidebar to show the new comment
      setShowVersionHistory(false);
      if (!sidebarOpen) setSidebarOpen(true);
    },
    [artifact, activeVersion, pendingAnchor, sidebarOpen, handleCommentsChanged],
  );

  const handleSelectionCancel = useCallback(() => {
    setPendingAnchor(null);
    setSelectionRect(null);
  }, []);

  const handleDownload = useCallback(() => {
    if (!artifact || !accessToken) return;
    posthog.capture('artifact_downloaded', {
      artifact_id: artifact.id,
      artifact_title: artifact.title,
      version: activeVersion,
      content_type: contentType,
    });
    window.open(
      `${ARTIFACT_ORIGIN}/download/${accessToken}/${artifact.id}/${activeVersionId || artifact.latest_version_id}`,
      '_blank',
    );
  }, [accessToken, activeVersion, activeVersionId, artifact, contentType]);

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 1500);
    });
  }, []);

  const revealPresentExit = useCallback(() => {
    setPresentExitVisible(true);
    if (presentExitTimerRef.current != null) {
      window.clearTimeout(presentExitTimerRef.current);
    }
    presentExitTimerRef.current = window.setTimeout(() => {
      setPresentExitVisible(false);
      presentExitTimerRef.current = null;
    }, 2200);
  }, []);

  useEffect(() => {
    if (presenting) {
      revealPresentExit();
    } else {
      setPresentExitVisible(false);
      if (presentExitTimerRef.current != null) {
        window.clearTimeout(presentExitTimerRef.current);
        presentExitTimerRef.current = null;
      }
    }
    return () => {
      if (presentExitTimerRef.current != null) {
        window.clearTimeout(presentExitTimerRef.current);
        presentExitTimerRef.current = null;
      }
    };
  }, [presenting, revealPresentExit]);

  const handleHighlightHandled = useCallback(() => {
    setScrollToCommentId(null);
  }, []);

  const handleOpenCommentsChange = useCallback((comments: Comment[]) => {
    setOpenComments(comments);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div
          className="w-[32px] h-[32px] border-[3px] rounded-full animate-spin"
          style={{
            borderColor: 'var(--rd-border)',
            borderTopColor: 'var(--rd-text-medium)',
          }}
        />
      </div>
    );
  }

  if (error || !artifact) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <i
            className="icon icon-app-warning"
            style={{
              fontSize: 48,
              color: 'var(--rd-text-faint)',
            }}
          />
          <p
            className="mt-[16px] text-[16px] font-semibold"
            style={{ color: 'var(--rd-text-strong)' }}
          >
            Artifact not found
          </p>
        </div>
      </div>
    );
  }

  // Determine the bottom of the selection for positioning the composer below
  const composerPosition = selectionRect
    ? {
        x: selectionRect.x + selectionRect.width / 2,
        y: selectionRect.y + selectionRect.height,
      }
    : { x: 0, y: 0 };

  return (
    <div className="flex flex-col h-full" ref={containerRef} style={{ background: 'var(--rd-surface-panel)' }}>
      {/* Header bar — full width on page background */}
      <div
        className="flex items-center h-[52px] min-h-[52px] px-[16px] gap-[12px] flex-shrink-0"
        style={{
          background: 'var(--rd-surface-panel)',
          borderBottom: '1px solid var(--rd-border-subtle)',
        }}
      >
        <button
          onClick={() => navigate('/')}
          className="w-[32px] h-[32px] flex items-center justify-center rounded-[8px] cursor-pointer border-0 bg-transparent transition-colors duration-[120ms]"
          style={{ color: 'var(--rd-avatar-default)' }}
          aria-label="Back to dashboard"
          title="Back to dashboard"
          onMouseEnter={(e) =>
            (e.currentTarget.style.background = 'var(--rd-surface-hover)')
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = 'transparent')
          }
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 3L5 8L10 13" />
          </svg>
        </button>

        <div className="min-w-0 flex items-center gap-[8px] flex-1">
          <h1
            className="text-[14px] font-semibold truncate min-w-0"
            style={{ color: 'var(--rd-text-strong)', letterSpacing: '-0.005em' }}
          >
            {artifact.title}
          </h1>
          {/* File type badge */}
          <span
            className="inline-flex items-center h-[17px] px-[5px] rounded-[3px] text-[10px] font-bold flex-shrink-0"
            style={{ ...fileTypeBadgeStyles[fileType], letterSpacing: '0.04em' }}
          >
            {fileType}
          </span>
          {(cspViolations.blocked.length > 0 || cspViolations.nonBlocking.length > 0) && (
            <div className="relative flex-shrink-0">
              <button
                onClick={() => setCspPopoverOpen(!cspPopoverOpen)}
                className="w-[28px] h-[28px] flex items-center justify-center rounded-[8px] cursor-pointer border-0 bg-transparent transition-colors duration-[120ms]"
                style={{ color: 'var(--rd-text-meta)' }}
                title="Some resources were blocked by security policy"
                aria-label="Security policy warnings"
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = 'var(--rd-surface-hover)')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = 'transparent')
                }
              >
                <i className="icon icon-app-warning" style={{ fontSize: 16 }} />
              </button>
              {cspPopoverOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setCspPopoverOpen(false)}
                  />
                  <div
                    className="absolute left-0 top-full mt-[4px] z-20 w-[320px] rounded-[10px] p-[16px] shadow-lg"
                    style={{
                      background: 'var(--rd-surface-panel)',
                      border: '1px solid var(--rd-border)',
                    }}
                  >
                    <p
                      className="text-[13px] font-semibold m-0 mb-[8px]"
                      style={{ color: 'var(--rd-text-strong)' }}
                    >
                      Content Security Policy
                    </p>
                    {cspViolations.blocked.length > 0 && (
                      <div className="mb-[8px]">
                        <p
                          className="text-[13px] font-semibold m-0 mb-[4px]"
                          style={{ color: 'var(--rd-text-subtle)' }}
                        >
                          Blocked ({cspViolations.blocked.length})
                        </p>
                        <ul className="m-0 pl-[16px] text-[13px]" style={{ color: 'var(--rd-text-subtle)' }}>
                          {cspViolations.blocked.map((r, i) => (
                            <li key={i} className="truncate">{r}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {cspViolations.nonBlocking.length > 0 && (
                      <div className="mb-[8px]">
                        <p
                          className="text-[13px] font-semibold m-0 mb-[4px]"
                          style={{ color: 'var(--rd-text-subtle)' }}
                        >
                          Non-blocking ({cspViolations.nonBlocking.length})
                        </p>
                        <ul className="m-0 pl-[16px] text-[13px]" style={{ color: 'var(--rd-text-subtle)' }}>
                          {cspViolations.nonBlocking.map((r, i) => (
                            <li key={i} className="truncate">{r}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {cspViolations.prompt && (
                      <div className="mt-[12px]">
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(cspViolations.prompt);
                          }}
                          className="w-full h-[32px] rounded-[8px] text-[13px] font-semibold cursor-pointer border transition-colors duration-[120ms]"
                          style={{
                            background: 'var(--rd-surface-hover)',
                            borderColor: 'var(--rd-border)',
                            color: 'var(--rd-accent)',
                          }}
                          title="Copy fix prompt to clipboard"
                        >
                          Copy fix prompt
                        </button>
                        <p
                          className="text-[13px] m-0 mt-[4px] text-center"
                          style={{ color: 'var(--rd-text-faint)' }}
                        >
                          Paste into Claude to update this artifact
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Edited timestamp */}
        {artifact.updated_at && (
          <span
            className="text-[12px] flex-shrink-0 hidden lg:inline whitespace-nowrap"
            style={{ color: 'var(--rd-text-meta)' }}
          >
            Edited {relativeTime(artifact.updated_at)}
            {artifact.author?.name ? ` · ${artifact.author.name}` : ''}
          </span>
        )}

        {visibleVersions.length > 1 && (
          <div
            className="hidden md:flex items-center gap-[1px] p-[2px] rounded-[7px] flex-shrink-0"
            style={{ background: 'var(--rd-surface-hover)' }}
          >
            {visibleVersions.map((version) => {
              const active = version.version_number === activeVersion;
              return (
                <button
                  key={version.id}
                  type="button"
                  onClick={() => handleVersionChange(version.version_number)}
                  className="h-[22px] px-[8px] rounded-[5px] border-0 cursor-pointer text-[12px] transition-all duration-[120ms]"
                  style={{
                    background: active ? 'var(--rd-surface-panel)' : 'transparent',
                    color: active ? 'var(--rd-text-strong)' : 'var(--rd-text-meta)',
                    fontWeight: active ? 600 : 500,
                    boxShadow: active ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (!active) e.currentTarget.style.color = 'var(--rd-text-strong)';
                  }}
                  onMouseLeave={(e) => {
                    if (!active) e.currentTarget.style.color = 'var(--rd-text-meta)';
                  }}
                >
                  v{version.version_number}
                </button>
              );
            })}
          </div>
        )}

        <div className="ml-auto flex items-center gap-[4px]">
          {/* Star */}
          <button
            onClick={handleToggleStar}
            disabled={starring}
            className="w-[28px] h-[28px] flex items-center justify-center rounded-[6px] cursor-pointer border-0 bg-transparent transition-[background-color,color] duration-[120ms]"
            style={{ color: starred ? 'var(--rd-star)' : 'var(--rd-text-subtle)' }}
            title={starred ? 'Remove star' : 'Star artifact'}
            aria-label={starred ? 'Remove star' : 'Star artifact'}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--rd-surface-hover)'; if (!starred) e.currentTarget.style.color = 'var(--rd-text-strong)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = starred ? 'var(--rd-star)' : 'var(--rd-text-subtle)'; }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill={starred ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round">
              <path d="M8 1.5l2 4.1 4.5.6-3.3 3.2.8 4.5L8 11.8l-4 2.1.8-4.5L1.5 6.2l4.5-.6z" />
            </svg>
          </button>

          {/* Present */}
          <button
            type="button"
            onClick={() => setPresenting(true)}
            className="w-[28px] h-[28px] flex items-center justify-center rounded-[6px] cursor-pointer border-0 bg-transparent transition-[background-color,color] duration-[120ms]"
            style={{ color: 'var(--rd-text-subtle)' }}
            title="Present"
            aria-label="Present"
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--rd-surface-hover)'; e.currentTarget.style.color = 'var(--rd-text-strong)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--rd-text-subtle)'; }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 3h12v8H2z" />
              <path d="M5.5 13.5h5M8 11.5v2" />
              <path d="M6 8l2-2 2 2" />
            </svg>
          </button>

          {/* Comments toggle with count badge */}
          <button
            type="button"
            onClick={() => { setShowVersionHistory(false); setSidebarOpen((current) => !current); }}
            className="w-[28px] h-[28px] flex items-center justify-center rounded-[6px] cursor-pointer border-0 transition-[background-color,color] duration-[120ms] relative"
            style={{
              background: sidebarOpen ? 'var(--rd-surface-hover)' : 'transparent',
              color: sidebarOpen ? 'var(--rd-text-strong)' : 'var(--rd-text-subtle)',
            }}
            title="Comments"
            aria-label="Comments"
            onMouseEnter={(e) => { if (!sidebarOpen) { e.currentTarget.style.background = 'var(--rd-surface-hover)'; e.currentTarget.style.color = 'var(--rd-text-strong)'; } }}
            onMouseLeave={(e) => { if (!sidebarOpen) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--rd-text-subtle)'; } }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 3h12v8H5l-3 3V3z" />
            </svg>
            {artifact.comment_count > 0 && (
              <span
                className="absolute flex items-center justify-center rounded-full text-white font-bold"
                style={{
                  top: -2, right: -3,
                  fontSize: 9, lineHeight: 1,
                  padding: '1px 4px',
                  background: 'var(--rd-text-strong)',
                  minWidth: 14, height: 14,
                  border: '1.5px solid #ffffff',
                }}
              >
                {artifact.comment_count}
              </span>
            )}
          </button>

          <span aria-hidden="true" style={{ width: 1, height: 18, background: 'var(--rd-border)', margin: '0 6px' }} />

          {/* Share — primary action: filled black pill with chevron menu */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShareMenuOpen((o) => !o)}
              className="inline-flex items-center gap-[5px] h-[28px] rounded-[6px] border-0 cursor-pointer text-[12px] font-semibold transition-colors duration-[120ms]"
              style={{
                background: 'var(--rd-text-strong)',
                color: '#ffffff',
                padding: '0 10px 0 11px',
                letterSpacing: '-0.005em',
              }}
              title="Share"
              aria-label="Share"
              aria-haspopup="menu"
              aria-expanded={shareMenuOpen}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#000000')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--rd-text-strong)')}
            >
              Share
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 6l4 4 4-4" />
              </svg>
            </button>
            {shareMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-[70]"
                  onClick={() => setShareMenuOpen(false)}
                />
                <div
                  role="menu"
                  className="absolute right-0 z-[80] w-[256px] p-[5px] rounded-[8px]"
                  style={{
                    top: 'calc(100% + 6px)',
                    background: 'var(--rd-surface-panel)',
                    border: '1px solid var(--rd-border)',
                    boxShadow: '0 8px 24px rgba(20,28,40,0.10), 0 2px 6px rgba(20,28,40,0.04)',
                  }}
                >
                  <button
                    role="menuitem"
                    onClick={() => { handleCopyLink(); }}
                    className="flex w-full items-center gap-[9px] px-[9px] py-[6px] rounded-[5px] border-0 cursor-pointer text-left bg-transparent"
                    style={{ color: 'var(--rd-text-strong)', fontSize: 12.5 }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--rd-surface-hover)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <span style={{ color: 'var(--rd-text-meta)', display: 'inline-flex' }}>
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M7 9a2.5 2.5 0 0 0 3.5 0l2-2a2.5 2.5 0 0 0-3.5-3.5l-1 1" />
                        <path d="M9 7a2.5 2.5 0 0 0-3.5 0l-2 2a2.5 2.5 0 0 0 3.5 3.5l1-1" />
                      </svg>
                    </span>
                    <span className="flex-1">{linkCopied ? 'Link copied' : 'Copy link'}</span>
                  </button>
                  <button
                    role="menuitem"
                    onClick={() => { setShareMenuOpen(false); setShareOpen(true); }}
                    className="flex w-full items-center gap-[9px] px-[9px] py-[6px] rounded-[5px] border-0 cursor-pointer text-left bg-transparent"
                    style={{ color: 'var(--rd-text-strong)', fontSize: 12.5 }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--rd-surface-hover)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <span style={{ color: 'var(--rd-text-meta)', display: 'inline-flex' }}>
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="6" cy="6" r="2.5" />
                        <path d="M2 13c0-2.2 1.8-4 4-4s4 1.8 4 4" />
                        <path d="M11 5h3M12.5 3.5v3" />
                      </svg>
                    </span>
                    <span className="flex-1">Invite people…</span>
                  </button>
                  <div style={{ height: 1, background: 'var(--rd-border-subtle)', margin: '3px 5px' }} />
                  <button
                    role="menuitem"
                    onClick={() => { setShareMenuOpen(false); handleDownload(); }}
                    className="flex w-full items-center gap-[9px] px-[9px] py-[6px] rounded-[5px] border-0 cursor-pointer text-left bg-transparent"
                    style={{ color: 'var(--rd-text-strong)', fontSize: 12.5 }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--rd-surface-hover)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <span style={{ color: 'var(--rd-text-meta)', display: 'inline-flex' }}>
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M8 2v9M4.5 7.5L8 11l3.5-3.5M3 13h10" />
                      </svg>
                    </span>
                    <span className="flex-1">Download</span>
                    <span style={{ fontSize: 11, color: 'var(--rd-text-faint)' }}>{`.${fileType.toLowerCase()}`}</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main content area: iframe + overlay sidebar */}
      <div className="flex flex-1 min-h-0 flex-col md:flex-row relative" style={{ background: 'var(--rd-surface-page)' }}>
        {/* Iframe container — card style matching viewer mockup */}
        <div className="flex-1 min-w-0 relative overflow-hidden p-[12px]">
          <div
            className="absolute inset-[12px] overflow-hidden rounded-[8px]"
            style={{
              background: 'var(--rd-surface-panel)',
              boxShadow: '0 0 0 1px var(--rd-border), 0 1px 2px rgba(0,0,0,0.03)',
            }}
          >
          {!iframeLoaded && (
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                background: 'var(--rd-surface-input)',
              }}
            >
              <div
                className="w-[32px] h-[32px] border-[3px] rounded-full animate-spin"
                style={{
                  borderColor: 'var(--rd-border)',
                  borderTopColor: 'var(--rd-text-medium)',
                }}
              />
            </div>
          )}
          {isPdf ? (
            /* PDFs: use embed without sandbox — Chrome blocks PDF viewer in sandboxed iframes */
            <embed
              src={iframeSrc}
              type="application/pdf"
              title={artifact.title}
              className="w-full h-full border-0"
              onLoad={() => setIframeLoaded(true)}
              style={{}}
            />
          ) : (
            /* HTML/MD: sandboxed iframe with postMessage bridge */
            <iframe
              ref={iframeRef}
              src={iframeSrc}
              title={artifact.title}
              className="w-full h-full border-0"
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
              onLoad={() => {
                setIframeLoaded(true);
                if (isMarkdown) {
                  postToIframe({ type: 'exhibit:set-theme', payload: { theme: resolvedTheme } });
                }
              }}
              style={{}}
            />
          )}

          {!isPdf && iframeLoaded && (
            <CommentMarkers
              comments={openComments}
              onMarkerClick={handleMarkerClick}
              onMarkerHover={handleMarkerHover}
              onMarkerLeave={handleLeaveAnchor}
              resolvedPositions={resolvedPositions}
              hasResolverResponded={hasResolverResponded}
              iframeScrollY={iframeScroll.scrollY}
              iframeViewportHeight={iframeScroll.viewportHeight}
              highlightedCommentId={highlightedCommentId}
            />
          )}

          {commentMode && !isPdf && iframeLoaded && (
            <div
              className="absolute top-[14px] left-1/2 z-40 flex -translate-x-1/2 items-center gap-[6px] rounded-full px-[11px] py-[5px] text-[12px] font-semibold pointer-events-none"
              style={{
                background: 'var(--rd-surface-panel)',
                border: '1px solid var(--rd-border)',
                boxShadow: '0 2px 8px rgba(20,28,40,0.06), 0 1px 2px rgba(20,28,40,0.04)',
                color: 'var(--rd-text-strong)',
              }}
            >
              <span className="h-[6px] w-[6px] rounded-full" style={{ background: '#2563EB' }} />
              Click a visible element to comment
              <span
                className="ml-[6px] rounded-[4px] px-[5px] py-px text-[11px] font-medium"
                style={{ background: 'var(--rd-surface-highlight)', color: 'var(--rd-text-meta)' }}
              >
                Esc
              </span>
            </div>
          )}

          {!isPdf && iframeLoaded && (
            <CommentModeToolbar
              commentMode={commentMode}
              onSelectCursor={selectCursorTool}
              onSelectComment={selectCommentTool}
            />
          )}

          {/* Inline selection composer (replaces old SelectionToolbar) */}
          {selectionRect && pendingAnchor && !isPdf && (
            <SelectionComposer
              visible={!!selectionRect}
              position={composerPosition}
              selectedText={
                pendingAnchor.type === 'text'
                  ? pendingAnchor.text
                  : pendingAnchor.type === 'element'
                    ? pendingAnchor.label ?? 'Selected element'
                    : ''
              }
              onSubmit={handleInlineCommentSubmit}
              onCancel={handleSelectionCancel}
              members={members}
            />
          )}
          </div>
        </div>

        {/* Sidebar — desktop: slide-over overlay, sibling of the card */}
        <div
          className="hidden md:flex absolute top-[12px] bottom-[12px] right-[12px] z-10 flex-col overflow-hidden rounded-r-[8px]"
          style={{
            width: 360,
            transform: showDesktopSidePanel ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 0.25s ease',
            background: 'var(--rd-surface-panel)',
            borderLeft: '1px solid var(--rd-border-subtle)',
            boxShadow: '-4px 0 16px rgba(0,0,0,0.04)',
          }}
        >
          {showVersionHistory ? (
            <VersionHistory
              artifactId={id!}
              versions={artifact.versions ?? []}
              currentVersionId={artifact.latest_version_id ?? artifact.versions?.[0]?.id ?? ''}
              onPreview={handleVersionPreview}
              onRestore={handleRestoreVersion}
              onClose={() => setShowVersionHistory(false)}
            />
          ) : (
            <CommentSidebar
              artifactId={id!}
              currentUserId={meData?.user?.id}
              currentVersion={activeVersion}
              collapsed={false}
              onToggle={() => setSidebarOpen(false)}
              onCommentsChanged={handleCommentsChanged}
              highlightedCommentId={highlightedCommentId}
              scrollToCommentId={scrollToCommentId}
              onHighlightHandled={handleHighlightHandled}
              onCommentFocus={handleSidebarCommentFocus}
              onCommentHover={handleSidebarCommentHover}
              onCommentLeave={handleSidebarCommentLeave}
              onOpenCommentsChange={handleOpenCommentsChange}
              refreshNonce={commentRefreshKey}
              members={members}
            />
          )}
        </div>

        {/* Mobile section (below iframe) — either comments or version history */}
        {showStackedPanel && (
          <div
            className={`${shouldAutoCollapse ? '' : 'md:hidden'} border-t`}
            style={{ borderColor: 'var(--rd-border)' }}
          >
          {showVersionHistory ? (
            <VersionHistory
              artifactId={id!}
              versions={artifact.versions ?? []}
              currentVersionId={artifact.latest_version_id ?? artifact.versions?.[0]?.id ?? ''}
              onPreview={handleVersionPreview}
              onRestore={handleRestoreVersion}
              onClose={() => setShowVersionHistory(false)}
            />
          ) : (
            <CommentSidebar
              artifactId={id!}
              currentUserId={meData?.user?.id}
              currentVersion={activeVersion}
              collapsed={false}
              onToggle={() => setSidebarOpen(false)}
              onCommentsChanged={handleCommentsChanged}
              highlightedCommentId={highlightedCommentId}
              scrollToCommentId={scrollToCommentId}
              onHighlightHandled={handleHighlightHandled}
              onCommentFocus={handleSidebarCommentFocus}
              onCommentHover={handleSidebarCommentHover}
              onCommentLeave={handleSidebarCommentLeave}
              onOpenCommentsChange={handleOpenCommentsChange}
              refreshNonce={commentRefreshKey}
              members={members}
            />
          )}
          </div>
        )}
      </div>

      <ShareDialog
        artifactId={id!}
        open={shareOpen}
        members={members}
        artifactAuthor={artifact.author}
        onClose={() => setShareOpen(false)}
      />

      {presenting && iframeSrc && (
        <div
          className="fixed inset-0 z-[200] flex flex-col"
          style={{ background: '#000' }}
          onMouseMove={revealPresentExit}
        >
          {isPdf ? (
            <embed
              src={iframeSrc}
              type="application/pdf"
              title={artifact.title}
              className="w-full flex-1 border-0"
              style={{ background: '#fff' }}
            />
          ) : (
            <iframe
              src={iframeSrc}
              title={artifact.title}
              className="w-full flex-1 border-0"
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
              style={{ background: '#fff' }}
            />
          )}
          <button
            type="button"
            onClick={() => setPresenting(false)}
            onMouseEnter={revealPresentExit}
            className="absolute top-[16px] right-[16px] inline-flex items-center gap-[6px] h-[32px] px-[12px] rounded-[8px] cursor-pointer border-0 text-[12px] font-semibold"
            style={{
              background: 'rgba(255,255,255,0.95)',
              color: '#1a1d21',
              boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
              opacity: presentExitVisible ? 1 : 0,
              pointerEvents: presentExitVisible ? 'auto' : 'none',
              transition: 'opacity 240ms ease',
            }}
            aria-label="Exit present mode"
            aria-hidden={!presentExitVisible}
          >
            Exit present
            <span
              className="inline-flex items-center px-[5px] py-px rounded-[4px] text-[10px] font-medium"
              style={{ background: 'rgba(0,0,0,0.08)' }}
            >
              Esc
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
